import { createFileRoute, Link } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { LegalLayout } from "@/components/legal/legal-layout";
import { SELLER } from "@/content/legal";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/refund-policy")({
  head: () => ({
    meta: [
      { title: "Refund Policy | Smart Word Labs" },
      {
        name: "description",
        content:
          "Smart Word Labs offers a 30-day money-back guarantee on monthly English tuition plans. Learn how to request a refund through our payment provider.",
      },
      { property: "og:title", content: "Refund Policy | Smart Word Labs" },
      {
        property: "og:description",
        content: "30-day money-back guarantee on Smart Word Labs monthly plans.",
      },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}/refund-policy` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/refund-policy` }],
    scripts: [
      breadcrumbJsonLd([{ name: "Refund Policy", path: "/refund-policy" }]),
    ],
  }),
  component: RefundPage,
});

function RefundPage() {
  const { t } = useTranslation("legal");
  const howToItems = t("refund.howTo.items", { returnObjects: true }) as string[];

  return (
    <LegalLayout title={t("refund.title")} updated={t("refund.updated")} path="/refund-policy">
      <p>
        <Trans
          i18nKey="refund.intro"
          ns="legal"
          values={{ legalName: SELLER.legalName, refundDays: SELLER.refundDays }}
          components={{ strong: <strong /> }}
        />
      </p>

      <h2>{t("refund.window.heading")}</h2>
      <p>{t("refund.window.body", { refundDays: SELLER.refundDays })}</p>

      <h2>{t("refund.howTo.heading")}</h2>
      <ul>
        {howToItems.map((item, i) => (
          <li key={i}>
            {i === 0 ? (
              <Trans
                i18nKey="refund.howTo.items.0"
                ns="legal"
                components={[<Link to="/contact" key="0" />]}
              />
            ) : (
              item
            )}
          </li>
        ))}
      </ul>

      <h2>{t("refund.cancelling.heading")}</h2>
      <p>{t("refund.cancelling.body")}</p>

      <h2>{t("refund.renewals.heading")}</h2>
      <p>{t("refund.renewals.body", { refundDays: SELLER.refundDays })}</p>

      <h2>{t("refund.processing.heading")}</h2>
      <p dangerouslySetInnerHTML={{ __html: t("refund.processing.body") }} />
    </LegalLayout>
  );
}
